/**
 * ChecklistCategoryDetail.js
 *
 * Detail view for a single checklist category in Settings.
 * OPTION 3: Shows list of checklists with EDIT buttons (configuration mode)
 */

import React, { useState, useMemo } from 'react';
import {
  ArrowLeft,
  Settings,
  AlertTriangle,
  Anchor,
  Users,
  Compass,
  Calendar,
  Clock,
  PlusCircle,
  Info,
  Pencil,
  Wrench,
  Clipboard
} from 'lucide-react';
import { CHECKLIST_IMPORT_TYPES } from '../../../../config/operations/checklistImportConfig';
import { usePreDepartureConfig } from '../../../../hooks/domain/operations/usePreDepartureConfig';
import { useChecklistConfig } from '../../../../hooks/domain/operations/useChecklistConfig';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useDailyChecksConfig } from '../../../../hooks/domain/operations/useDailyChecksConfig';
import {
  NAVIGATION_CHECKLISTS,
  OPERATIONAL_CHECKLISTS,
  EMERGENCY_CHECKLISTS
} from '../../../../config/operations/checklistDefinitions';
import ChecklistItemsView from '../checklists/ChecklistItemsView';
import ChecklistEditModal from '../checklists/ChecklistEditModal';
import DailyChecksSettingsView from './DailyChecksSettingsView';
// FT-356: register onBack with the shell-level MobileBottomBar via context so
// users can escape settings sub-views without navigating via the header.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

// Icon mapping
const ICON_MAP = {
  AlertTriangle,
  Anchor,
  Users,
  Compass,
  Settings,
  Calendar,
  Clock,
  PlusCircle
};

// Color mapping
const COLOR_MAP = {
  red: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
  green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' },
  cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200' }
};

/**
 * Pre-Departure Detail View (existing functionality)
 */
const PreDepartureDetailView = ({ onImport }) => {
  const {
    items,
    categories,
    addItem,
    updateItem,
    removeItem,
    lastModified,
    modifiedBy
  } = usePreDepartureConfig();

  const [showEditModal, setShowEditModal] = useState(false);

  return (
    <>
      <ChecklistItemsView
        items={items}
        categoryLabel="Pre-Departure"
        onEdit={() => setShowEditModal(true)}
        onImport={onImport}
        lastModified={lastModified}
        modifiedBy={modifiedBy}
        canEdit={true}
      />

      <ChecklistEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        items={items}
        categories={categories}
        categoryLabel="Pre-Departure"
        onAddItem={addItem}
        onEditItem={(item) => updateItem(item.id, item)}
        onRemoveItem={removeItem}
      />
    </>
  );
};

/**
 * Checklist Detail View with Edit functionality
 * Uses useChecklistConfig hook for managing checklist items
 */
const ChecklistDetailView = ({ checklist, categoryConfig, onBack, onImport }) => {
  const { can } = usePermissions();
  const colors = COLOR_MAP[categoryConfig?.color] || COLOR_MAP.slate;
  const [showEditModal, setShowEditModal] = useState(false);
  // FT-356: onBack here goes back to the category's checklist list — register
  // it as the shell-level back handler so the MobileBottomBar can fire it.
  useSetBackHandler(onBack);

  // Use the generic checklist config hook for this specific checklist
  const {
    items,
    addItem,
    updateItem,
    removeItem,
    lastModified,
    modifiedBy
  } = useChecklistConfig(checklist.id, checklist.items || []);

  // RBAC check
  const canEdit = useMemo(() => {
    try {
      if (!can || typeof can !== 'function') return true;
      const hasPermission = can('update', 'settings') || can('read', 'settings');
      return hasPermission !== false;
    } catch {
      return true;
    }
  }, [can]);

  // Categories for the edit modal (simple default)
  const categories = useMemo(() => [
    { id: 'general', label: 'General' },
    { id: 'safety', label: 'Safety' },
    { id: 'navigation', label: 'Navigation' },
    { id: 'equipment', label: 'Equipment' }
  ], []);

  return (
    <div className="space-y-4">
      {/* Back to list */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to {categoryConfig?.label} list
      </button>

      {/* Checklist header */}
      <div className="flex items-center gap-3 pb-4 border-b border-slate-200">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
          {checklist.icon ? (
            <checklist.icon className={`h-5 w-5 ${colors.text}`} />
          ) : (
            <Clipboard className={`h-5 w-5 ${colors.text}`} />
          )}
        </div>
        <div>
          <h4 className="font-medium text-slate-800">{checklist.name}</h4>
          <p className="text-sm text-slate-500">{checklist.description}</p>
        </div>
      </div>

      {/* Items view */}
      <ChecklistItemsView
        items={items}
        categoryLabel={checklist.name}
        onEdit={() => setShowEditModal(true)}
        onImport={onImport}
        lastModified={lastModified}
        modifiedBy={modifiedBy}
        canEdit={canEdit && checklist.isCustomisable}
      />

      {/* Edit Modal - REAL implementation */}
      <ChecklistEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        items={items}
        categories={categories}
        categoryLabel={checklist.name}
        onAddItem={addItem}
        onEditItem={(item) => updateItem(item.id, item)}
        onRemoveItem={removeItem}
      />
    </div>
  );
};

/**
 * Generic Category Detail View - Shows list of checklists with Edit buttons
 */
const GenericCategoryDetailView = ({ categoryConfig, checklists, onImport }) => {
  const { can } = usePermissions();
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const colors = COLOR_MAP[categoryConfig?.color] || COLOR_MAP.slate;

  // RBAC check - if user can access settings, they can edit
  // Note: can(action, resource) - action first, resource second
  const canEdit = useMemo(() => {
    try {
      if (!can || typeof can !== 'function') return true; // Default to true if no RBAC
      const hasPermission = can('update', 'settings') ||
                           can('read', 'settings');
      return hasPermission !== false; // Default to true unless explicitly denied
    } catch {
      return true;
    }
  }, [can]);

  const handleEditChecklist = (checklist) => {
    setSelectedChecklist(checklist);
  };

  // If a checklist is selected, show its detail view with edit capability
  if (selectedChecklist) {
    return (
      <ChecklistDetailView
        checklist={selectedChecklist}
        categoryConfig={categoryConfig}
        onBack={() => setSelectedChecklist(null)}
        onImport={onImport}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Checklist List */}
      <div className="space-y-2">
        {checklists.map(checklist => {
          const ChecklistIcon = checklist.icon || Clipboard;
          return (
            <div
              key={checklist.id}
              className={`flex items-center justify-between p-4 bg-white border rounded-xl ${colors.border}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
                  <ChecklistIcon className={`h-5 w-5 ${colors.text}`} />
                </div>
                <div>
                  <p className="font-medium text-slate-800">{checklist.name}</p>
                  <p className="text-sm text-slate-500">
                    {checklist.items?.length || 0} items
                    {checklist.regulatory && ` - ${checklist.regulatory}`}
                  </p>
                </div>
              </div>

              {canEdit && (
                <button
                  onClick={() => handleEditChecklist(checklist)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium
                    text-slate-700 bg-slate-100 border border-slate-200 rounded-lg
                    hover:bg-slate-200 transition-colors"
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </button>
              )}
            </div>
          );
        })}
      </div>

      {checklists.length === 0 && (
        <div className="text-center py-8 text-slate-500">
          <p>No checklists configured for this category.</p>
        </div>
      )}
    </div>
  );
};

/**
 * Maintenance Module Redirect View - For Daily/Periodic
 */
const MaintenanceRedirectView = ({ categoryConfig }) => {
  return (
    <div className="text-center py-12 px-6 bg-amber-50 rounded-xl border-2 border-dashed border-amber-200">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
        <Wrench className="h-8 w-8 text-amber-600" />
      </div>
      <h4 className="text-lg font-medium text-amber-900 mb-2">
        Use Maintenance Module
      </h4>
      <p className="text-sm text-amber-700 max-w-md mx-auto">
        {categoryConfig?.label} checks are managed in the <strong>Maintenance</strong> module.
        This provides better scheduling, tracking, and compliance features for routine inspections.
      </p>
      <p className="text-xs text-amber-600 mt-4">
        Navigate to: Equipment {'>'} Maintenance
      </p>
    </div>
  );
};

/**
 * Main ChecklistCategoryDetail Component
 */
const ChecklistCategoryDetail = ({ categoryId, categoryConfig, onBack, onImport }) => {
  const Icon = ICON_MAP[categoryConfig?.icon] || Settings;
  const colors = COLOR_MAP[categoryConfig?.color] || COLOR_MAP.slate;
  // FT-356: register the category-detail onBack (which returns to the settings
  // category grid) as the shell-level back handler for this screen.
  useSetBackHandler(onBack);

  // FT-80: Get daily checks config from hook (includes user edits and enabled states)
  const { getCategoriesForSettings } = useDailyChecksConfig();
  const dailyChecksChecklists = getCategoriesForSettings();

  // Get checklists for category
  const checklists = useMemo(() => {
    switch (categoryId) {
      case CHECKLIST_IMPORT_TYPES.NAVIGATION:
        return NAVIGATION_CHECKLISTS;
      case CHECKLIST_IMPORT_TYPES.OPERATIONAL:
        return OPERATIONAL_CHECKLISTS;
      case CHECKLIST_IMPORT_TYPES.EMERGENCY:
        return EMERGENCY_CHECKLISTS;
      case CHECKLIST_IMPORT_TYPES.DAILY:
        // FT-80: Return daily checks from config hook (includes user edits)
        return dailyChecksChecklists;
      default:
        return [];
    }
  }, [categoryId, dailyChecksChecklists]);

  // Render appropriate detail view based on category
  const renderDetailView = () => {
    switch (categoryId) {
      case CHECKLIST_IMPORT_TYPES.PRE_DEPARTURE:
        return <PreDepartureDetailView onImport={onImport} />;

      case CHECKLIST_IMPORT_TYPES.NAVIGATION:
      case CHECKLIST_IMPORT_TYPES.OPERATIONAL:
      case CHECKLIST_IMPORT_TYPES.EMERGENCY:
        return (
          <GenericCategoryDetailView
            categoryConfig={categoryConfig}
            checklists={checklists}
            onImport={onImport}
          />
        );

      case CHECKLIST_IMPORT_TYPES.DAILY:
        // FT-80 Phase 3: Use purpose-built settings view with full save functionality
        return <DailyChecksSettingsView onImport={onImport} />;

      case CHECKLIST_IMPORT_TYPES.PERIODIC:
        return <MaintenanceRedirectView categoryConfig={categoryConfig} />;

      default:
        return (
          <div className="text-center py-12 px-6 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
            <Settings className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">Configuration coming soon for {categoryConfig?.label}.</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-slate-600" />
        </button>

        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors.bg}`}>
          <Icon className={`h-6 w-6 ${colors.text}`} />
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900">{categoryConfig?.label}</h3>
          <p className="text-sm text-gray-500">{categoryConfig?.description}</p>
        </div>
      </div>

      {/* Category-specific content */}
      {renderDetailView()}

      {/* Info box */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">About checklist changes</p>
          <p className="mt-1">
            Changes here only affect NEW operations. Existing records keep their
            original checklist items for audit purposes. All modifications are logged.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChecklistCategoryDetail;
